/**
 * PlanningPresence.tsx — Composant de présence unifié (Bloc 1B).
 *
 * Réutilisable dans TOUTES les briques (service technique, scolaire, petite
 * enfance, urbanisme, grc, rh). Reproduit fidèlement les mockups validés :
 *   - Vue équipe   : une ligne par personne, présence colorée par jour, survol
 *                    = horaire ; badge cumul « + aussi <service> ».
 *   - Vue personne : menu déroulant → un planning détaillé (blocs horaires).
 *   - Vue année    : grille pivotée 12 colonnes (mois) × 31 lignes (jours),
 *                    lisible mobile.
 *
 * Le périmètre de visibilité (agent = lui-même, manager = son service, admin =
 * tout) est appliqué CÔTÉ SERVEUR : ce composant affiche ce que l'API renvoie.
 *
 * L'accent (couleur module) est passé en prop `accent` ; les classes Tailwind
 * correspondantes sont listées en dur dans ACCENTS pour survivre au purge.
 */

import { useEffect, useMemo, useState } from 'react';
import type {
  PlanningAnneeResponse,
  PlanningAnneeStatut,
} from '@roronoa-collectivites/shared-types';

// Types locaux souples : structurellement compatibles avec le retour de l'API
// planning (dont certains champs sont typés `string` plutôt qu'en unions).
type PlanningAgent = {
  id: string;
  fullName: string;
  email: string;
  role: string;
  couleurMarker: string | null;
};
type PlanningCreneau = {
  id: string;
  agentUserId: string;
  dateDebut: string;
  dateFin: string;
  titre: string;
  couleurMarker: string | null;
  statut?: string;
  profilId?: string | null;
  signalementId?: string | null;
};
type PlanningWeekResponse = {
  weekStart: string;
  weekEnd: string;
  agents: PlanningAgent[];
  creneaux: PlanningCreneau[];
  absences?: Array<{ agentUserId: string; dateDebut: string; dateFin: string; type: string }>;
};

// ── Accents par module (classes en dur pour le purge Tailwind) ──────────────
export type PlanningAccent = 'tech' | 'scolaire' | 'grc' | 'rh' | 'petite_enfance' | 'urbanisme' | 'asso' | 'comptable';

const ACCENTS: Record<PlanningAccent, { seg: string; segRing: string; text: string; tint: string; border: string }> = {
  tech:           { seg: 'bg-tech text-white',           segRing: 'ring-tech',           text: 'text-tech-fg',           tint: 'bg-tech-tint',           border: 'border-tech/40' },
  scolaire:       { seg: 'bg-scolaire text-white',       segRing: 'ring-scolaire',       text: 'text-scolaire-fg',       tint: 'bg-scolaire-tint',       border: 'border-scolaire/40' },
  grc:            { seg: 'bg-grc text-white',            segRing: 'ring-grc',            text: 'text-grc-fg',            tint: 'bg-grc-tint',            border: 'border-grc/40' },
  rh:             { seg: 'bg-rh text-white',             segRing: 'ring-rh',             text: 'text-rh-fg',             tint: 'bg-rh-tint',             border: 'border-rh/40' },
  petite_enfance: { seg: 'bg-petite_enfance text-white', segRing: 'ring-petite_enfance', text: 'text-petite_enfance-fg', tint: 'bg-petite_enfance-tint', border: 'border-petite_enfance/40' },
  urbanisme:      { seg: 'bg-urba text-white',           segRing: 'ring-urba',           text: 'text-urba-fg',           tint: 'bg-urba-tint',           border: 'border-urba/40' },
  asso:           { seg: 'bg-asso text-white',           segRing: 'ring-asso',           text: 'text-asso-fg',           tint: 'bg-asso-tint',           border: 'border-asso/40' },
  comptable:      { seg: 'bg-compta text-white',         segRing: 'ring-compta',         text: 'text-compta-fg',         tint: 'bg-compta-tint',         border: 'border-compta/40' },
};

const FRENCH_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];
const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

type VueMode = 'equipe' | 'personne' | 'annee';

export interface PlanningPresenceProps {
  accent: PlanningAccent;
  /** charge la semaine (agents + créneaux) — l'API applique le périmètre */
  loadWeek: (weekStartISO: string) => Promise<PlanningWeekResponse>;
  /** charge la vue année d'une personne */
  loadAnnee: (annee: number, agentId?: string) => Promise<PlanningAnneeResponse>;
  /** rôle courant (pour savoir si on montre le sélecteur de personne) */
  role: string;
  /** id du user courant (pour la vue personne par défaut) */
  currentUserId: string;
  /** si fourni, un clic sur une case ouvre la déclaration d'absence (managers/admin) */
  onDeclareAbsence?: (agent: { id: string; fullName: string }, jourISO: string) => void;
  /** si fourni, charge les compteurs RH (RTT, récup) pour la vue année */
  loadCompteurs?: (annee: number, agentId?: string) => Promise<Array<{ type: string; libelle: string; solde: number; unite: string }>>;
}

// ── Helpers dates (timezone-safe, jamais toISOString pour la logique) ────────
function mondayOf(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay() === 0 ? 7 : x.getDay();
  x.setDate(x.getDate() - (day - 1));
  return x;
}
function localISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}h${d.getMinutes() ? String(d.getMinutes()).padStart(2, '0') : ''}`;
}
function sameDay(iso: string, d: Date): boolean {
  const x = new Date(iso);
  return x.getFullYear() === d.getFullYear() && x.getMonth() === d.getMonth() && x.getDate() === d.getDate();
}

export function PlanningPresence({ accent, loadWeek, loadAnnee, role, currentUserId, onDeclareAbsence, loadCompteurs }: PlanningPresenceProps) {
  const A = ACCENTS[accent];
  const [mode, setMode] = useState<VueMode>(role === 'agent' ? 'personne' : 'equipe');
  const [weekStart, setWeekStart] = useState<Date>(() => mondayOf(new Date()));
  const [data, setData] = useState<PlanningWeekResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<string>(currentUserId);

  // Vue année
  const [annee, setAnnee] = useState<number>(new Date().getFullYear());
  const [anneeData, setAnneeData] = useState<PlanningAnneeResponse | null>(null);
  const [anneeLoading, setAnneeLoading] = useState(false);
  const [compteursRH, setCompteursRH] = useState<Array<{ type: string; libelle: string; solde: number; unite: string }>>([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    loadWeek(localISODate(weekStart))
      .then((res) => {
        if (!alive) return;
        setData(res);
        // sélectionne la 1re personne visible si l'actuelle n'est pas dans la liste
        if (!res.agents.some((a) => a.id === selectedPerson) && res.agents[0]) {
          setSelectedPerson(res.agents[0].id);
        }
      })
      .catch((e) => alive && setError(e?.message ?? 'Erreur de chargement'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  useEffect(() => {
    if (mode !== 'annee') return;
    let alive = true;
    setAnneeLoading(true);
    loadAnnee(annee, role === 'agent' ? undefined : selectedPerson)
      .then((res) => alive && setAnneeData(res))
      .catch(() => alive && setAnneeData(null))
      .finally(() => alive && setAnneeLoading(false));
    if (loadCompteurs) {
      loadCompteurs(annee, role === 'agent' ? undefined : selectedPerson)
        .then((c) => alive && setCompteursRH(c))
        .catch(() => alive && setCompteursRH([]));
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, annee, selectedPerson]);

  const days = useMemo(() => FRENCH_DAYS.map((label, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return { label, date: d };
  }), [weekStart]);

  const shiftWeek = (n: number) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + n * 7);
    setWeekStart(mondayOf(d));
  };

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 4);
    const f = (d: Date) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    return `Semaine du ${f(weekStart)} — ${f(end)}`;
  }, [weekStart]);

  const showPersonPicker = role !== 'agent';

  return (
    <div className="rounded-r-xl border border-border bg-surface/55 backdrop-blur-glass shadow-soft">
      {/* ── Barre de contrôle : segmented + navigation ── */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="inline-flex gap-0.5 rounded-r-lg border border-border bg-bg-2/60 p-0.5">
          {(['equipe', 'personne', 'annee'] as VueMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={[
                'rounded-r-md px-3 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2',
                A.segRing,
                mode === m ? A.seg : 'text-ink-muted hover:text-ink',
              ].join(' ')}
            >
              {m === 'equipe' ? '▦ Équipe' : m === 'personne' ? '☰ Personne' : '▤ Année'}
            </button>
          ))}
        </div>

        {showPersonPicker && (mode === 'personne' || mode === 'annee') && data && (
          <select
            value={selectedPerson}
            onChange={(e) => setSelectedPerson(e.target.value)}
            className="rounded-r-md border border-border-hi bg-bg-2/60 px-3 py-2 text-xs font-semibold text-ink focus-visible:outline-none"
            aria-label="Choisir une personne"
          >
            {data.agents.map((a) => (
              <option key={a.id} value={a.id}>{a.fullName} — {a.role === 'manager' ? 'manager' : 'agent'}</option>
            ))}
          </select>
        )}

        <div className="ml-auto flex items-center gap-2">
          {mode === 'annee' ? (
            <>
              <button type="button" onClick={() => setAnnee((y) => y - 1)} className="h-8 w-8 rounded-r-md border border-border-hi bg-bg-2/60 text-ink">‹</button>
              <span className="min-w-[52px] text-center text-sm font-bold text-ink-muted">{annee}</span>
              <button type="button" onClick={() => setAnnee((y) => y + 1)} className="h-8 w-8 rounded-r-md border border-border-hi bg-bg-2/60 text-ink">›</button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => shiftWeek(-1)} className="h-8 w-8 rounded-r-md border border-border-hi bg-bg-2/60 text-ink">‹</button>
              <span className="min-w-[160px] text-center text-xs font-semibold text-ink-muted">{weekLabel}</span>
              <button type="button" onClick={() => shiftWeek(1)} className="h-8 w-8 rounded-r-md border border-border-hi bg-bg-2/60 text-ink">›</button>
            </>
          )}
        </div>
      </div>

      {/* ── Corps ── */}
      {loading && mode !== 'annee' ? (
        <div className="p-8 text-center text-sm text-ink-muted">Chargement du planning…</div>
      ) : error ? (
        <div className="p-8 text-center text-sm text-urgent">{error}</div>
      ) : mode === 'equipe' ? (
        <VueEquipe data={data!} days={days} accent={A} onDeclareAbsence={onDeclareAbsence} />
      ) : mode === 'personne' ? (
        <VuePersonne data={data!} days={days} personId={selectedPerson} accent={A} />
      ) : (
        <VueAnnee data={anneeData} loading={anneeLoading} accent={A} compteursRH={compteursRH} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VUE ÉQUIPE — une ligne par personne, présence colorée par jour
// ═══════════════════════════════════════════════════════════════════════════
function VueEquipe({
  data, days, accent, onDeclareAbsence,
}: {
  data: PlanningWeekResponse;
  days: Array<{ label: string; date: Date }>;
  accent: typeof ACCENTS[PlanningAccent];
  onDeclareAbsence?: (agent: { id: string; fullName: string }, jourISO: string) => void;
}) {
  const today = new Date();
  const creneauxOf = (agentId: string, date: Date): PlanningCreneau[] =>
    // Planning UNIFIÉ : tout créneau posé (présence pure OU travail) = l'agent est
    // présent sur ce créneau. On ne filtre plus sur profilId — c'est ce qui créait
    // deux plannings distincts et le bug « travail l'après-midi sans présence ».
    data.creneaux.filter((c) => c.agentUserId === agentId && sameDay(c.dateDebut, date) && c.statut !== 'annule');
  // Congé validé (RH) chevauchant ce jour → prioritaire sur la présence.
  const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const libelleType = (t: string) => t === 'conges_payes' ? 'Congé' : t === 'rtt' ? 'RTT' : t === 'maladie' ? 'Maladie' : t === 'sans_solde' ? 'Congé' : t === 'recuperation' ? 'Récup.' : 'Absent';
  const congeOf = (agentId: string, date: Date): string | null => {
    const j = ymd(date);
    const abs = (data.absences ?? []).find((a) => a.agentUserId === agentId && a.dateDebut <= j && a.dateFin >= j);
    return abs ? libelleType(abs.type) : null;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="border-b border-border px-4 py-3 text-left text-xs font-semibold uppercase tracking-r-eyebrow text-ink-subtle">Équipe</th>
            {days.map((d, i) => {
              const isToday = sameDay(d.date.toISOString(), today);
              return (
                <th key={i} className={['border-b border-border px-2 py-3 text-center text-2xs font-semibold uppercase tracking-r-eyebrow', isToday ? accent.text : 'text-ink-subtle'].join(' ')}>
                  {d.label} {d.date.getDate()}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.agents.map((a) => (
            <tr key={a.id}>
              <td className="border-b border-border px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: a.couleurMarker ?? '#A8A29E' }} />
                  <div className="flex flex-col leading-tight">
                    <span className="font-semibold text-ink">{a.fullName}</span>
                    <span className="text-2xs text-ink-subtle">{a.role === 'manager' ? 'Manager' : 'Agent'}</span>
                  </div>
                </div>
              </td>
              {days.map((d, i) => {
                const conge = congeOf(a.id, d.date);
                const crs = creneauxOf(a.id, d.date)
                  .slice()
                  .sort((x, y) => new Date(x.dateDebut).getTime() - new Date(y.dateDebut).getTime());
                const present = !conge && crs.length > 0;
                // Affiche chaque plage distincte (« 8h-12h · 13h-17h »), pause
                // comprise, en dédupliquant les plages identiques (sécurité si
                // d'anciens doublons subsistent en base).
                const plagesUniques = Array.from(new Set(crs.map((c) => `${hhmm(c.dateDebut)}–${hhmm(c.dateFin)}`)));
                const plages = plagesUniques.join(' · ');
                const titre = present ? (crs[0]!.titre || 'Présent') : '';
                return (
                  <td key={i} className="border-b border-border p-1.5">
                    <div
                      className={['group relative mx-auto flex h-9 items-center justify-center rounded-r-md px-1 text-center text-[10px] font-semibold leading-tight', onDeclareAbsence ? 'cursor-pointer' : ''].join(' ')}
                      onClick={onDeclareAbsence ? () => onDeclareAbsence({ id: a.id, fullName: a.fullName }, ymd(d.date)) : undefined}
                      title={onDeclareAbsence ? 'Cliquer pour déclarer une absence' : undefined}
                      style={conge ? {
                        backgroundColor: 'color-mix(in oklab, oklch(0.82 0.16 85) 22%, transparent)',
                        border: '1px solid color-mix(in oklab, oklch(0.82 0.16 85) 50%, transparent)',
                        color: 'oklch(0.88 0.13 85)',
                      } : present ? {
                        backgroundColor: 'color-mix(in oklab, oklch(0.72 0.18 155) 22%, transparent)',
                        border: '1px solid color-mix(in oklab, oklch(0.72 0.18 155) 45%, transparent)',
                        color: 'oklch(0.90 0.10 155)',
                      } : {
                        border: '1px dashed oklch(var(--c-border-hi))',
                        color: 'oklch(var(--c-ink-dim))',
                      }}
                    >
                      {conge ? conge : present ? plages : '—'}
                      {present && (
                        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-r-md border border-border-hi bg-surface-hi px-2.5 py-1.5 text-2xs font-medium text-ink opacity-0 shadow-pop transition group-hover:translate-y-0 group-hover:opacity-100">
                          <span className={accent.text}>{plages}</span>{titre && titre !== 'Présent' ? ` · ${titre}` : ''}
                        </span>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
          {data.agents.length === 0 && (
            <tr><td colSpan={days.length + 1} className="px-4 py-8 text-center text-sm text-ink-muted">Aucune personne dans ce périmètre</td></tr>
          )}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-4 border-t border-border px-4 py-3 text-2xs text-ink-muted">
        <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded" style={{ background: 'color-mix(in oklab, oklch(0.72 0.18 155) 30%, transparent)', border: '1px solid oklch(0.72 0.18 155)' }} /> Présent (survol = horaire)</span>
        <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded" style={{ background: 'color-mix(in oklab, oklch(0.82 0.16 85) 30%, transparent)', border: '1px solid oklch(0.82 0.16 85)' }} /> Congé / absence</span>
        <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded border border-dashed border-border-hi" /> Non planifié</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VUE PERSONNE — un planning détaillé (blocs horaires par jour)
// ═══════════════════════════════════════════════════════════════════════════
function VuePersonne({
  data, days, personId, accent,
}: {
  data: PlanningWeekResponse;
  days: Array<{ label: string; date: Date }>;
  personId: string;
  accent: typeof ACCENTS[PlanningAccent];
}) {
  const person = data.agents.find((a) => a.id === personId) ?? data.agents[0];
  const HOURS = [7, 9, 11, 13, 15, 17];
  const SLOT_PX = 40; // px par heure (2h = 80px)
  const START_HOUR = 7;

  const creneaux = data.creneaux.filter((c) => c.agentUserId === (person?.id ?? '') && c.statut !== 'annule');
  // Un créneau tombant un jour de congé validé ne compte pas dans les heures présentes.
  const estEnConge = (iso: string) => (data.absences ?? []).some((a) => a.agentUserId === (person?.id ?? '') && a.dateDebut <= iso && a.dateFin >= iso);
  const isoOf = (d: string) => new Date(d).toISOString().slice(0, 10);
  const totalH = creneaux
    .filter((c) => !estEnConge(isoOf(c.dateDebut)))
    .reduce((acc, c) => acc + (new Date(c.dateFin).getTime() - new Date(c.dateDebut).getTime()) / 3600000, 0);

  if (!person) return <div className="p-8 text-center text-sm text-ink-muted">Aucune personne à afficher</div>;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3.5">
        <span className="grid h-10 w-10 place-items-center rounded-full text-sm font-bold text-white" style={{ backgroundColor: person.couleurMarker ?? '#A8A29E' }}>
          {person.fullName.split(' ').map((w) => w[0]).slice(0, 2).join('')}
        </span>
        <div>
          <h3 className="text-sm font-bold text-ink">{person.fullName}</h3>
          <p className="text-2xs text-ink-subtle">{person.role === 'manager' ? 'Manager' : 'Agent'}</p>
        </div>
        <div className="ml-auto text-right">
          <div className={['text-xl font-bold', accent.text].join(' ')}>{Math.round(totalH)} h</div>
          <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">planifiées · semaine</div>
        </div>
      </div>

      <div className="overflow-x-auto p-2">
        <div className="grid min-w-[560px]" style={{ gridTemplateColumns: '48px repeat(5, 1fr)' }}>
          <div className="border-b border-border" />
          {days.map((d, i) => (
            <div key={i} className="border-b border-border py-2 text-center text-2xs font-semibold uppercase tracking-r-eyebrow text-ink-muted">
              {d.label} {d.date.getDate()}
            </div>
          ))}

          <div className="flex flex-col">
            {HOURS.map((h) => (
              <div key={h} className="pr-2 text-right text-[10px] text-ink-dim" style={{ height: SLOT_PX * 2 }}>{h}h</div>
            ))}
          </div>

          {days.map((d, dayIdx) => {
            const dayCreneaux = creneaux.filter((c) => sameDay(c.dateDebut, d.date));
            // Congé validé ce jour → bandeau jaune couvrant la colonne.
            const jourISO = `${d.date.getFullYear()}-${String(d.date.getMonth() + 1).padStart(2, '0')}-${String(d.date.getDate()).padStart(2, '0')}`;
            const abs = (data.absences ?? []).find((a) => a.agentUserId === (person?.id ?? '') && a.dateDebut <= jourISO && a.dateFin >= jourISO);
            const congeLabel = abs ? (abs.type === 'conges_payes' ? 'Congé' : abs.type === 'injustifiee' ? 'Absent' : abs.type === 'justifiee' ? 'Absent justifié' : abs.type) : null;
            return (
              <div key={dayIdx} className="relative border-l border-border/60" style={{ height: SLOT_PX * 2 * HOURS.length }}>
                {HOURS.map((_, i) => (
                  <div key={i} className="border-b border-border/40" style={{ height: SLOT_PX * 2 }} />
                ))}
                {congeLabel ? (
                  <div className="absolute inset-x-1 top-1 bottom-1 flex items-center justify-center rounded-r-md text-xs font-semibold"
                    style={{ backgroundColor: 'color-mix(in oklab, oklch(0.82 0.16 85) 20%, transparent)', border: '1px solid color-mix(in oklab, oklch(0.82 0.16 85) 45%, transparent)', color: 'oklch(0.88 0.13 85)' }}>
                    {congeLabel}
                  </div>
                ) : dayCreneaux.map((c) => {
                  const start = new Date(c.dateDebut);
                  const end = new Date(c.dateFin);
                  const top = (start.getHours() + start.getMinutes() / 60 - START_HOUR) * SLOT_PX;
                  const height = Math.max(24, ((end.getTime() - start.getTime()) / 3600000) * SLOT_PX);
                  return (
                    <div key={c.id} className="absolute left-1 right-1 overflow-hidden rounded-r-md px-2 py-1.5 text-white shadow-soft"
                      style={{ top, height, backgroundColor: c.couleurMarker ?? person.couleurMarker ?? '#378ADD' }}>
                      <div className="truncate text-[10.5px] font-semibold">{c.titre || 'Créneau'}</div>
                      <div className="text-[10px] opacity-85">{hhmm(c.dateDebut)} – {hhmm(c.dateFin)}</div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VUE ANNÉE — grille pivotée 12 colonnes (mois) × 31 lignes (jours)
// ═══════════════════════════════════════════════════════════════════════════
const STATUT_STYLE: Record<PlanningAnneeStatut, { bg: string; label: string }> = {
  present:       { bg: 'color-mix(in oklab, oklch(0.72 0.18 155) 55%, transparent)', label: 'Présent' },
  demi:          { bg: 'linear-gradient(135deg, color-mix(in oklab, oklch(0.72 0.18 155) 55%, transparent) 50%, color-mix(in oklab, oklch(0.80 0.17 75) 45%, transparent) 50%)', label: '½ journée' },
  conge:         { bg: 'color-mix(in oklab, oklch(0.80 0.17 75) 55%, transparent)', label: 'Congé' },
  absence:       { bg: 'color-mix(in oklab, oklch(0.68 0.22 25) 55%, transparent)', label: 'Absence' },
  ferie:         { bg: 'color-mix(in oklab, oklch(0.70 0.15 230) 45%, transparent)', label: 'Férié' },
  weekend:       { bg: 'oklch(var(--c-bg-2))', label: 'Week-end' },
  non_planifie:  { bg: 'color-mix(in oklab, white 3%, transparent)', label: 'Non planifié' },
};

function VueAnnee({ data, loading, accent, compteursRH }: { data: PlanningAnneeResponse | null; loading: boolean; accent: typeof ACCENTS[PlanningAccent]; compteursRH?: Array<{ type: string; libelle: string; solde: number; unite: string }> }) {
  if (loading) return <div className="p-8 text-center text-sm text-ink-muted">Chargement de l'année…</div>;
  if (!data) return <div className="p-8 text-center text-sm text-ink-muted">Année indisponible</div>;

  // Indexe les jours par date
  const byDate = new Map(data.jours.map((j) => [j.date, j]));
  const nowMonth = new Date().getMonth();
  // Compteurs RH (RTT, récup HS, récup férié) — toujours affichés, même à 0.
  const rhAffiches = (compteursRH ?? []).filter((c) => c.type !== 'conges');

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3.5">
        <span className="grid h-9 w-9 place-items-center rounded-full text-sm font-bold text-white" style={{ backgroundColor: '#1D9E75' }}>
          {data.agent.fullName.split(' ').map((w) => w[0]).slice(0, 2).join('')}
        </span>
        <div>
          <h3 className="text-sm font-bold text-ink">{data.agent.fullName}</h3>
          <p className="text-2xs text-ink-subtle">{data.agent.role === 'manager' ? 'Manager' : 'Agent'}</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Counter n={data.compteurs.presence} l="présence" color="oklch(0.82 0.16 155)" />
          <Counter n={data.compteurs.conges} l="congés" color="oklch(0.85 0.15 75)" />
          <Counter n={data.compteurs.absences} l="absences" color="oklch(0.78 0.18 25)" />
          {rhAffiches.map((c) => (
            <Counter key={c.type} n={c.solde} l={c.libelle.replace('Récupération ', 'récup. ')} color="oklch(0.72 0.16 250)" suffix={c.unite === 'heure' ? 'h' : ''} />
          ))}
        </div>
      </div>

      <div className="overflow-x-auto px-3 py-3">
        <table className="mx-auto" style={{ borderCollapse: 'separate', borderSpacing: 2 }}>
          <thead>
            <tr>
              <th className="w-4" />
              {MONTHS.map((m, i) => (
                <th key={m} className={['h-5 text-center text-[10px] font-bold', i === nowMonth ? accent.text : 'text-ink-muted'].join(' ')} style={{ minWidth: 26 }}>{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 31 }, (_, di) => {
              const day = di + 1;
              return (
                <tr key={day}>
                  <th className="pr-1.5 text-right text-[8.5px] font-medium text-ink-dim">{day}</th>
                  {MONTHS.map((_, mi) => {
                    const date = new Date(data.annee, mi, day);
                    if (date.getMonth() !== mi) {
                      return <td key={mi}><div style={{ width: 24, height: 14, borderRadius: 3, background: 'color-mix(in oklab, white 3%, transparent)' }} /></td>;
                    }
                    const key = `${data.annee}-${String(mi + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const j = byDate.get(key);
                    const st = j?.statut ?? 'non_planifie';
                    const style = STATUT_STYLE[st];
                    const label = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
                    return (
                      <td key={mi}>
                        <div className="group relative" style={{ width: 24, height: 14, borderRadius: 3, background: style.bg, cursor: 'default' }}>
                          <span className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1.5 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-r-md border border-border-hi bg-surface-hi px-2.5 py-1.5 text-[10.5px] font-medium text-ink opacity-0 shadow-pop transition group-hover:translate-y-0 group-hover:opacity-100" style={{ textAlign: 'left' }}>
                            {label}<br /><span className={accent.text}>{j?.libelle ?? style.label}</span>
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-3 border-t border-border px-4 py-3 text-2xs text-ink-muted">
        {(['present', 'demi', 'conge', 'absence', 'ferie', 'weekend'] as PlanningAnneeStatut[]).map((s) => (
          <span key={s} className="flex items-center gap-1.5">
            <i className="h-3 w-3 rounded" style={{ background: STATUT_STYLE[s].bg }} /> {STATUT_STYLE[s].label}
          </span>
        ))}
      </div>
    </div>
  );
}

function Counter({ n, l, color, suffix }: { n: number; l: string; color: string; suffix?: string }) {
  return (
    <div className="min-w-[56px] rounded-r-md border border-border bg-bg-2/60 px-2.5 py-1.5 text-center">
      <div className="text-base font-bold" style={{ color }}>{n}{suffix ?? ''}</div>
      <div className="text-[8.5px] uppercase tracking-r-eyebrow text-ink-subtle">{l}</div>
    </div>
  );
}
